// Portal — Home Page (Live API data)
import { useState, useEffect } from 'react';
import { Activity, Calendar, User, Bell, ChevronRight, Clock, Video, Stethoscope } from 'lucide-react';
import { Link } from 'react-router-dom';

interface NextAppt {
    id: string;
    scheduledStart: string;
    serviceType: string;
    appointmentType: string;
    status: string;
    doctor: { firstName: string; lastName: string; specialty: string | null; calendarColor: string };
}

interface PatientInfo {
    firstName: string;
    lastName: string;
}

import { API_BASE } from '../config/api';

export function HomePage() {
    const [nextAppt, setNextAppt] = useState<NextAppt | null>(null);
    const [patient, setPatient] = useState<PatientInfo | null>(null);
    const [loading, setLoading] = useState(true);

    const greeting = (() => {
        const h = new Date().getHours();
        if (h < 12) return 'Buenos días';
        if (h < 18) return 'Buenas tardes';
        return 'Buenas noches';
    })();

    useEffect(() => {
        loadData();
    }, []);

    async function loadData() {
        setLoading(true);
        try {
            // Get the first patient for demo mode
            const patientRes = await fetch(`${API_BASE}/patients?limit=1`);
            const patientJson = await patientRes.json();
            const pt = patientJson.data?.[0];
            if (pt) {
                setPatient({ firstName: pt.firstName, lastName: pt.lastName });

                // Get upcoming appointment
                const apptRes = await fetch(`${API_BASE}/appointments?patientId=${pt.id}&status=booked,confirmed&limit=1`);
                const apptJson = await apptRes.json();
                const appts = apptJson.data || [];
                // Find the next upcoming one
                const now = new Date();
                const upcoming = appts.filter((a: any) => new Date(a.scheduledStart) > now)
                    .sort((a: any, b: any) => new Date(a.scheduledStart).getTime() - new Date(b.scheduledStart).getTime());
                if (upcoming.length > 0) {
                    setNextAppt(upcoming[0]);
                }
            }
        } catch {
            console.warn('API unavailable');
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="fade-in">
            <div className="portal-header">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <h1>{greeting}, {patient?.firstName || 'Paciente'}</h1>
                        <p>Bienvenid{patient?.firstName?.endsWith('a') ? 'a' : 'o'} a su portal de salud</p>
                    </div>
                    <div style={{
                        width: 44, height: 44, borderRadius: 'var(--radius-full)',
                        background: 'rgba(255,255,255,0.2)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                    }}>
                        <Bell size={20} />
                    </div>
                </div>
            </div>

            <div className="portal-content">
                {/* Next Appointment */}
                <div className="portal-card" style={{
                    background: 'linear-gradient(135deg, var(--color-primary-50), #fff)',
                    border: '1px solid var(--color-primary-100)',
                }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-primary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                        Próxima cita
                    </div>
                    {loading ? (
                        <div style={{ height: 60 }}><div className="skeleton" style={{ height: 50 }} /></div>
                    ) : nextAppt ? (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <div style={{ fontWeight: 600, fontSize: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: nextAppt.doctor.calendarColor, display: 'inline-block' }} />
                                    Dr. {nextAppt.doctor.firstName} {nextAppt.doctor.lastName}
                                </div>
                                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                                    {nextAppt.doctor.specialty || 'Medicina General'}
                                </div>
                                <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <Clock size={12} />
                                    {new Date(nextAppt.scheduledStart).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' })}
                                    {' — '}
                                    {new Date(nextAppt.scheduledStart).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}
                                    {nextAppt.appointmentType === 'telehealth' && (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, color: 'var(--color-primary)' }}>
                                            <Video size={12} /> Tele
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div style={{
                                width: 52, height: 52, borderRadius: 'var(--radius-lg)',
                                background: 'var(--color-primary-100)', display: 'flex',
                                alignItems: 'center', justifyContent: 'center',
                                color: 'var(--color-primary)',
                            }}>
                                <Calendar size={24} />
                            </div>
                        </div>
                    ) : (
                        <div style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>
                            No tiene citas próximas — <Link to="/book" style={{ color: 'var(--color-primary)', fontWeight: 600 }}>Agendar una cita</Link>
                        </div>
                    )}
                </div>

                {/* Quick Actions */}
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, marginTop: 20 }}>Acceso rápido</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    {[
                        { icon: Calendar, label: 'Mis citas', to: '/appointments', color: '#0D7C7C' },
                        { icon: Activity, label: 'Mi historia', to: '/records', color: '#16A34A' },
                        { icon: User, label: 'Mi perfil', to: '/profile', color: '#F59E0B' },
                        { icon: Stethoscope, label: 'Agendar cita', to: '/book', color: '#DC2626' },
                    ].map((item, i) => (
                        <Link key={i} to={item.to} style={{ textDecoration: 'none', color: 'inherit' }}>
                            <div className="portal-card" style={{ textAlign: 'center', padding: '20px 12px', cursor: 'pointer' }}>
                                <div style={{
                                    width: 44, height: 44, borderRadius: 'var(--radius-lg)',
                                    background: `${item.color}15`, color: item.color,
                                    margin: '0 auto 8px', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <item.icon size={22} />
                                </div>
                                <div style={{ fontSize: 13, fontWeight: 600 }}>{item.label}</div>
                            </div>
                        </Link>
                    ))}
                </div>

                {/* Health Tips */}
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, marginTop: 20 }}>Salud y bienestar</div>
                {[
                    { title: 'Control preventivo', desc: 'Programe su chequeo anual de rutina', color: '#0D7C7C' },
                    { title: 'Vacunación COVID-19', desc: 'Verifique su esquema de vacunación', color: '#16A34A' },
                ].map((tip, i) => (
                    <div key={i} className="portal-card" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{
                            width: 8, height: 40, borderRadius: 4,
                            background: tip.color, flexShrink: 0,
                        }} />
                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>{tip.title}</div>
                            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{tip.desc}</div>
                        </div>
                        <ChevronRight size={18} color="var(--color-text-muted)" />
                    </div>
                ))}
            </div>
        </div>
    );
}
